// Copyright 2019 Fowlit. All rights reserved.
//
// Use of this source code is governed by the MIT license that can be found in
// the LICENSE.md file.

// SearchBar component with dropdopwn list.

import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Colors, Fonts, Dimensions, Media } from 'fowlit';
import { useComponentVisible } from '../../Hooks';

const quantityHeight = '70px';

const Container = styled.div`
  grid-area: guest;
  position: relative;
  background: ${props => props.theme.searchbarBackground};
  display: grid;
  grid-template-columns: ${quantityHeight} 1fr;
  grid-template-rows: ${quantityHeight} 1fr;
  grid-template-areas: 'icon label' 'dropdown dropdown';
  border: 2px solid ${props => props.theme.searchbarBorder};
  border-left: none;
  @media (max-width: ${Media.phone}) {
    border-bottom-left-radius: 0;
    border-top-right-radius: ${Dimensions.radius};
    border-left: 1px solid ${props => props.theme.searchbarBorder};
  }
`;

const Icon = styled.i`
  grid-area: icon;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${Fonts.sizeTiny};
  color: ${props => (props.active ? props.theme.searchbarActive : props.theme.searchbarColor)};
  transition: 200ms ease-in-out;
`;

const Label = styled.div`
  grid-area: label;
  color: ${props => props.theme.searchbarColor};
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-family: ${Fonts.text};
  font-size: ${Fonts.sizeSuperTiny};
  padding: 0 30px 0 0;
  cursor: pointer;

  i {
    transform: ${props => (props.active ? `rotateZ(180deg)` : `none`)};
    transition: 200ms ease-in-out;
    font-size: ${Fonts.sizeTiny};
  }
`;

const Dropdown = styled.div`
  background: ${props => props.theme.searchbarBackground};
  display: ${props => (props.active ? 'block' : 'none')};
  position: absolute;
  top: calc(${quantityHeight} - 2px);
  left: -2px;
  right: -2px;
  box-shadow: 1px 5px 5px -7px ${Colors.black};
  border-bottom-left-radius: ${Dimensions.radius};
  border-bottom-right-radius: ${Dimensions.radius};
  border-left: 2px solid ${props => props.theme.searchbarBorder};
  border-right: 2px solid ${props => props.theme.searchbarBorder};
  border-bottom: 2px solid ${props => props.theme.searchbarBorder};
  @media (max-width: ${Media.phone}) {
    grid-area: dropdown;
    top: -2px;
  }
`;

const Picker = styled.div`
  user-select: none;
  display: grid;
  grid-template-columns: 60px 1fr 60px;
  grid-template-rows: 60px;
  grid-template-areas: 'decrease value increace';
  color: ${props => props.theme.searchbarColor};
`;

const Decreace = styled.i`
  user-select: none;
  grid-area: decrease;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  font-family: ${Fonts.text};
  font-size: ${Fonts.sizeSuperTiny};
  border-bottom-left-radius: ${Dimensions.radius};
  &:hover {
    background: ${props => props.theme.searchbarHover};
  }
`;

const Value = styled.i`
  user-select: none;
  grid-area: value;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: ${Fonts.text};
`;

const Increace = styled.i`
  user-select: none;
  grid-area: increace;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border-bottom-right-radius: ${Dimensions.radius};
  &:hover {
    background: ${props => props.theme.searchbarHover};
  }
`;

const Quantity = ({ label, value, setValue }) => {
  const { ref, visible, setVisible } = useComponentVisible(false);
  return (
    <Container active={visible} ref={ref}>
      <Icon active={visible || value !== 0} className="icon-users" />
      <Label active={visible} onClick={() => setVisible(v => !v)}>
        {value === 0 ? label : value}
        <i className="icon-angle-down" />
      </Label>
      <Dropdown active={visible}>
        <Picker>
          <Decreace className="icon-minus" onClick={() => (value < 1 ? setValue(value) : setValue(value - 1))} />
          <Value>{value}</Value>
          <Increace className="icon-plus" onClick={() => (value > 100 ? setValue(value) : setValue(value + 1))} />
        </Picker>
      </Dropdown>
    </Container>
  );
};

Quantity.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  setValue: PropTypes.func.isRequired
};

export default Quantity;
